import Vue from 'vue';
import { MutationTree } from 'vuex';

import {
    IAIJobTrackerState,
    IAIJobTrackerGroup,
    IAIJobTrackerJob,
    IAIImageSessionState
} from './ai-job-tracker-states.store';
import aiJobTrackerTypesStore from './ai-job-tracker-types.store';

// Deleting a group also drops the page's minimized session once no other
// group needs it.
const removeGroup = (state: IAIJobTrackerState, groupKey: string): void => {
    const group = state.groups[groupKey];
    Vue.delete(state.groups, groupKey);
    if (group && !Object.values(state.groups).some(other => other.routePath === group.routePath)) {
        Vue.delete(state.sessions, group.routePath);
    }
};

const mutations: MutationTree<IAIJobTrackerState> = <MutationTree<IAIJobTrackerState>>{
    [aiJobTrackerTypesStore.Set.SET_TRACKER_GROUP]: (state: IAIJobTrackerState, payload: IAIJobTrackerGroup) => {
        Vue.set(state.groups, payload.groupKey, payload);
    },
    // Merge updates from the status endpoint into the tracked jobs, matched
    // by job UUID (globally unique per the contract). Besides status and
    // result, the source image/filename are backfilled — the minimize
    // payload may omit them, while the API rows always carry the original
    // image URL. Jobs whose values are unchanged keep their object identity
    // so no re-render is forced.
    [aiJobTrackerTypesStore.Set.UPDATE_TRACKER_JOBS]: (state: IAIJobTrackerState, payload: IAIJobTrackerJob[]) => {
        if (!payload?.length) return;
        const updatesById: Record<string, IAIJobTrackerJob> = {};
        payload.forEach(job => { updatesById[job.jobId] = job; });

        Object.values(state.groups).forEach(group => {
            let changed = false;
            const jobs = group.jobs.map(job => {
                const update = updatesById[job.jobId];
                if (!update) return job;
                const resultImageUrl = update.resultImageUrl ?? job.resultImageUrl;
                const imageUrl = job.imageUrl || update.imageUrl || null;
                const filename = job.filename || update.filename || null;
                if (update.status === job.status
                    && resultImageUrl === job.resultImageUrl
                    && imageUrl === job.imageUrl
                    && filename === job.filename) {
                    return job;
                }
                changed = true;
                return { ...job, status: update.status, resultImageUrl, imageUrl, filename };
            });
            if (changed) {
                group.jobs = jobs;
                if (!group.previewImageUrl) {
                    group.previewImageUrl = jobs.find(job => job.imageUrl)?.imageUrl ?? null;
                }
            }
        });
    },
    // Dismiss one batch; when it was the property's last group, its
    // minimized session goes with it.
    [aiJobTrackerTypesStore.Set.CLEAR_TRACKER_GROUP]: (state: IAIJobTrackerState, groupKey: string) => {
        removeGroup(state, groupKey);
    },
    // Drop a single job (saved or trashed by the user); an emptied group is
    // removed along with it.
    [aiJobTrackerTypesStore.Set.CLEAR_TRACKER_JOB]: (state: IAIJobTrackerState, jobId: string) => {
        const group = Object.values(state.groups)
            .find(candidate => candidate.jobs.some(job => job.jobId === jobId));
        if (!group) return;
        const jobs = group.jobs.filter(job => job.jobId !== jobId);
        if (jobs.length) {
            group.jobs = jobs;
        } else {
            removeGroup(state, group.groupKey);
        }
    },
    [aiJobTrackerTypesStore.Set.SET_AI_IMAGE_SESSION]: (state: IAIJobTrackerState, payload: { path: string, session: IAIImageSessionState }) => {
        Vue.set(state.sessions, payload.path, payload.session);
    },
    [aiJobTrackerTypesStore.Set.CLEAR_AI_IMAGE_SESSION]: (state: IAIJobTrackerState, path: string) => {
        Vue.delete(state.sessions, path);
    }
};

export default mutations;
